import { useCallback, useEffect, useState } from "react"
import { Button, Modal } from "react-bootstrap"
import { IRequisiteType } from "../../core/response.types"
import { newRequisiteType, updateRequisiteType } from "../../core/api"
import { RequisiteTypeBody } from "../../core/requests.types"
import Swal from "sweetalert2"
import Select from 'react-select'
import { SelectStyles } from "../../../../../../../components/Select"
import { useThemeMode } from "../../../../../../../_metronic/partials"
import { getErrorCode, getErrorText } from '../../../../../../common/api/errors'

type PMOption = { value: number; label: string };

interface Props {
  show: boolean
  onClose: (open: boolean) => void
  onSave?: (item: IRequisiteType) => void
  initialRequisiteType: string
  optionsPaymentMethod: any[]
  editingRequisiteType?: IRequisiteType | null
}

export default ({ show, onClose, onSave, initialRequisiteType, optionsPaymentMethod, editingRequisiteType }: Props) => {
    const { mode: theme } = useThemeMode()

    const isEditMode = Boolean(editingRequisiteType)

    const pmOptions: PMOption[] = optionsPaymentMethod.map((v) => ({
        value: v.id,
        label: v.type,
    }));

    // ===== ДАННЫЕ ФОРМЫ =====
    const [rtName, setRTName] = useState(initialRequisiteType)
    const [rtCode, setRTCode] = useState('')
    const [rtPM, setRTPM] = useState<number[]>([])

    // ===== UI СОСТОЯНИЯ =====
    const [isSaving, setIsSaving] = useState(false)
    
    const [forcedCode, setForcedCode] = useState(false)
    const [forcedPaymentMethod, setForcedPaymentMethod] = useState(false)

    const buildPayload = useCallback(
        (override?: Partial<RequisiteTypeBody>): RequisiteTypeBody => ({
            code: rtCode,
            name: rtName,
            paymentMethods: rtPM,
            ...(isEditMode
                ? {
                    forcedCode,
                    forcedPaymentMethod,
                }
                : {}),
            ...override,
        }),
        [rtCode, rtName, rtPM, forcedCode, forcedPaymentMethod, isEditMode]
    )

    const sendRequest = useCallback(
        async (payload: RequisiteTypeBody) => {
            return editingRequisiteType
                ? updateRequisiteType(editingRequisiteType.id, payload)
                : newRequisiteType(payload)
        },
        [editingRequisiteType]
    )

    const extractErrorInfo = (error: unknown) => {
        const generalError = getErrorText(
            error,
            (error instanceof Error ? error.message : '') || "Unknown error"
        )

        return {
            generalError,
            // Only payment methods still in use may be overridden; a code in use or referenced is shown as is.
            canForceCode: false,
            canForcePaymentMethod: getErrorCode(error) === "REQUISITE_TYPE_PAYMENT_METHODS_IN_USE",
        }
    }

    const askForceConfirmation = useCallback(
        async (
            message: string,
            canForceCode: boolean,
            canForcePaymentMethod: boolean
        ) => {
            const forceParts: string[] = []

            if (canForceCode) {
                forceParts.push("изменение кода")
            }

            if (canForcePaymentMethod) {
                forceParts.push("изменение платежных методов")
            }

            const forceText =
                forceParts.length > 0
                ? `\n\nДоступно принудительное сохранение: ${forceParts.join(", ")}.`
                : ""

            return Swal.fire({
                title: "Обнаружено использование",
                text: `${message}${forceText}`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Все равно сохранить",
                cancelButtonText: "Отмена",
                reverseButtons: true,
            })
        },
        []
    )
    
    const handleSave = useCallback(async () => {
        setIsSaving(true)
        try {
            const payload = buildPayload()

            const response = await sendRequest(payload)

            if (!response?.data?.success) {
                throw new Error(
                "errors" in response?.data
                    ? response?.data?.errors?.general_error
                    : "Unknown error"
                )
            }
                
            if (response.data.data) {
                onSave?.(response.data.data)
                onClose(false)
                return
            }

            throw new Error("Response data is empty")
        } catch (error: any) {
            const { generalError, canForceCode, canForcePaymentMethod } = extractErrorInfo(error)

            if (canForceCode || canForcePaymentMethod) {
                const confirmResult = await askForceConfirmation(
                    generalError,
                    canForceCode,
                    canForcePaymentMethod
                )

                if (confirmResult.isConfirmed) {
                    try {
                        const forcedPayload = buildPayload({
                            forcedCode: canForceCode ? true : forcedCode,
                            forcedPaymentMethod: canForcePaymentMethod ? true : forcedPaymentMethod,
                        })

                        const forcedResponse = await sendRequest(forcedPayload)

                        if (!forcedResponse?.data?.success) {
                            throw new Error(
                                "errors" in forcedResponse?.data
                                ? forcedResponse?.data?.errors?.general_error
                                : "Unknown error"
                            )
                        }

                        if (forcedResponse.data.data) {
                            if (canForceCode) {
                                setForcedCode(true)
                            }
                            if (canForcePaymentMethod) {
                                setForcedPaymentMethod(true)
                            }

                            onSave?.(forcedResponse.data.data)
                            onClose(false)
                            return
                        }

                        throw new Error("Response data is empty")
                    } catch (forcedError: unknown) {
                        const forcedMessage = getErrorText(
                            forcedError,
                            (forcedError instanceof Error ? forcedError.message : '') || "Unknown error"
                        )

                        Swal.fire({
                          title: "Ошибка",
                          text: "Не удалось выполнить принудительное сохранение: " + forcedMessage,
                          icon: "error",
                        })
                        console.error("Ошибка принудительного сохранения типа реквизита:", forcedError)
                        return
                    }
                }
            }

            Swal.fire({
              title: "Ошибка",
              text: "Произошла ошибка сохранения типа реквизита: " + generalError,
              icon: "error",
            })
            console.error("Ошибка сохранения типа реквизита:", error)
        } finally {
            setIsSaving(false)
        }
    }, [
        buildPayload,
        sendRequest,
        onSave,
        onClose,
        forcedCode,
        forcedPaymentMethod,
        askForceConfirmation,
    ])

    const handleSelectRequisiteType = (selected: readonly PMOption[] | null) => {
        setRTPM(selected ? selected.map((x) => x.value) : []);
    };

    // ===== СБРОС ПРИ ЗАКРЫТИИ / ЗАПОЛНЕНИЕ ПРИ РЕДАКТИРОВАНИИ =====    
    useEffect(() => {
        if (!show) {
            setRTName('')
            setRTCode('')
            setRTPM([])
            setForcedCode(false)
            setForcedPaymentMethod(false)
            return
        }

        if (editingRequisiteType) {
            setRTName(editingRequisiteType.name ?? '')
            setRTCode(editingRequisiteType.code ?? '')

            const selectedPaymentMethodIds = optionsPaymentMethod
                .filter((pm) =>
                    editingRequisiteType.paymentMethods?.includes(pm.type)
                )
                .map((pm) => pm.id)

            setRTPM(selectedPaymentMethodIds)
            setForcedCode(false)
            setForcedPaymentMethod(false)
            return
        }

        setRTName(initialRequisiteType ?? '')
        setRTCode('')
        setRTPM([])
        setForcedCode(false)
        setForcedPaymentMethod(false)
    }, [show, editingRequisiteType, initialRequisiteType, optionsPaymentMethod])

    // ===== ПРОВЕРКА ЗАПОЛНЕННОСТИ ФОРМЫ =====
    const isFormComplete = rtCode.trim().length > 2 && rtName.trim().length > 2

    return (
        <Modal 
            show={show} 
            onHide={() => {
                if (!isSaving) {
                    onClose(false)
                }
            }}
            centered
            onEscapeKeyDown={(e) => {
                if (isSaving) {
                    e.preventDefault()
                    return
                }
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title>{isEditMode ? 'Редактирование типа реквизита' : 'Новый тип реквизита'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>   
                <div className="fv-row mb-5">
                    <label className="required fs-6 fw-semibold mb-2">Тип реквизита</label>

                    <input
                        className="form-control form-control-solid"
                        placeholder="Например: Асме №1 Банк"
                        value={rtName}
                        onChange={(e) => setRTName(e.target.value)}
                        disabled={isSaving}
                    />

                    <div className="form-text text-muted">
                        Наименование типа реквизита.
                    </div>
                </div> 
                <div className="fv-row mb-5">
                    <label className="required fs-6 fw-semibold mb-2">Код</label>

                    <input
                        className={`form-control form-control-solid${isEditMode ? ' border border-danger': ''}`}
                        placeholder="Например: ACME_01_BANK"
                        value={rtCode}
                        onChange={(e) => setRTCode(e.target.value)}
                        disabled={isSaving}
                    />
                    {isEditMode && <div className="form-text text-danger">* Изменение может повлечь серьезные проблемы!!!</div>}
                    <div className="form-text text-muted">
                        Технический код типа реквизита. Всегда уникален для общего списка.
                    </div>                    
                </div> 

                <div className="fv-row mb-0">
                    <label className="required fs-6 fw-semibold mb-2">Платежные методы</label>
                    <Select  
                        styles={SelectStyles(theme)}       
                        placeholder={'Выберите платежные методы'} 
                        isMulti   
                        isClearable       
                        onChange={handleSelectRequisiteType}
                        value={pmOptions.filter((o) => rtPM.includes(o.value))}
                        options={pmOptions}
                        isDisabled={isSaving}
                    />
                    <div className="form-text text-muted">
                        Выберите платежные методы, для которых будет доступен этот тип реквизита.
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant='secondary' onClick={() => onClose(false)} disabled={isSaving}>Отменить</Button>
                <Button 
                    variant='primary' 
                    onClick={handleSave}
                    disabled={isSaving || !isFormComplete}
                >
                    {isSaving ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Сохранение...
                        </>
                    ) : (
                        'Сохранить'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}